import useMergedState from '../_util/hooks/useMergedState';
import padStart from 'lodash-es/padStart';
import { PickerPanel } from '../vc-picker';
import { useLocaleReceiver } from '../locale-provider/LocaleReceiver';
import zhCN from './locale/zh_CN';
import CalendarHeader from './Header';
import { computed, defineComponent, toRef } from 'vue';
import useConfigInject from '../_util/hooks/useConfigInject';
import classNames from '../_util/classNames';
import dayjs from 'dayjs';
import { QuotationColor } from '../_util/enum';
function generateCalendar(generateConfig) {
    function isSameYear(date1, date2) {
        return date1 && date2 && generateConfig.getYear(date1) === generateConfig.getYear(date2);
    }
    function isSameMonth(date1, date2) {
        return (isSameYear(date1, date2) && generateConfig.getMonth(date1) === generateConfig.getMonth(date2));
    }
    function isSameDate(date1, date2) {
        return (isSameMonth(date1, date2) && generateConfig.getDate(date1) === generateConfig.getDate(date2));
    }
    const Calendar = defineComponent({
        name: 'WdCalendar',
        inheritAttrs: false,
        props: [
            'prefixCls',
            'locale',
            'validRange',
            'disabledDate',
            'dateFullCellRender',
            'dataSource',
            'quotationColor',
            'hiddenWeekend',
            'height',
            'showAmount',
            'noEvent',
            'regionType',
            'dateCellRender',
            'monthFullCellRender',
            'monthCellRender',
            'headerRender',
            'subHeaderRender',
            'defaultHeaderLeftMoreRender',
            'value',
            'defaultValue',
            'mode',
            'fullscreen',
            'onChange',
            'onPanelChange',
            'onSelect',
            'valueFormat',
        ],
        slots: [
            'dateFullCellRender',
            'dateCellRender',
            'monthFullCellRender',
            'monthCellRender',
            'headerRender',
            'subHeaderRender',
            'defaultHeaderLeftMoreRender',
        ],
        setup(props, { emit, slots, attrs }) {
            const { prefixCls, direction } = useConfigInject('picker', props);
            const calendarPrefixCls = computed(() => `${prefixCls.value}-calendar`);
            const maybeToString = (date) => {
                return props.valueFormat ? generateConfig.toString(date, props.valueFormat) : date;
            };
            const value = computed(() => {
                if (props.value) {
                    return props.valueFormat
                        ? generateConfig.toDate(props.value, props.valueFormat)
                        : props.value;
                }
                return (props.value === '' ? undefined : props.value);
            });
            const defaultValue = computed(() => {
                if (props.defaultValue) {
                    return props.valueFormat
                        ? generateConfig.toDate(props.defaultValue, props.valueFormat)
                        : props.defaultValue;
                }
                return (props.defaultValue === '' ? undefined : props.defaultValue);
            });
            // Value
            const [mergedValue, setMergedValue] = useMergedState(() => value.value || generateConfig.getNow(props.regionType), {
                defaultValue: defaultValue.value,
                value,
            });
            // Mode
            const [mergedMode, setMergedMode] = useMergedState('month', {
                value: toRef(props, 'mode'),
            });
            const panelMode = computed(() => (mergedMode.value === 'year' ? 'month' : 'date'));
            const mergedDisabledDate = computed(() => {
                return (date) => {
                    const notInRange = props.validRange
                        ? generateConfig.isAfter(props.validRange[0], date) ||
                            generateConfig.isAfter(date, props.validRange[1])
                        : false;
                    return notInRange || !!props.disabledDate?.(date);
                };
            });
            // ====================== Events ======================
            const triggerPanelChange = (date, newMode) => {
                emit('panelChange', maybeToString(date), newMode);
            };
            const triggerChange = (date) => {
                setMergedValue(date);
                if (!isSameDate(date, mergedValue.value)) {
                    // Trigger when month panel switch month
                    if ((panelMode.value === 'date' && !isSameMonth(date, mergedValue.value)) ||
                        (panelMode.value === 'month' && !isSameYear(date, mergedValue.value))) {
                        triggerPanelChange(date, mergedMode.value);
                    }
                    const val = maybeToString(date);
                    emit('update:value', val);
                    emit('change', val);
                }
            };
            const onSelectCurMonth = () => {
                triggerChange(generateConfig.getNow(props.regionType));
            };
            const triggerModeChange = (newMode) => {
                setMergedMode(newMode);
                triggerPanelChange(mergedValue.value, newMode);
            };
            const onInternalSelect = (date) => {
                triggerChange(date);
                emit('select', maybeToString(date));
            };
            const getCurMonthValue = (formatDate, date, key, format = 'YYYY/MM/DD') => {
                const curItem = props.dataSource?.find(item => dayjs(item?.timestamp).format(format) === formatDate);
                if (key === 'valueText') {
                    if (curItem?.closeTheMarket) {
                        return mergedLocale.value.lang.closedMarket;
                    }
                }
                if (!curItem || props.disabledDate(date)) {
                    return '--';
                }
                return curItem[key];
            };
            // ====================== Locale ======================
            const defaultLocale = computed(() => {
                const { locale } = props;
                const result = {
                    ...zhCN,
                    ...locale,
                };
                result.lang = {
                    ...result.lang,
                    ...(locale || {}).lang,
                };
                return result;
            });
            const [mergedLocale] = useLocaleReceiver('Calendar', defaultLocale);
            return () => {
                const today = generateConfig.getNow(props.regionType);
                const { dateFullCellRender = slots?.dateFullCellRender, dateCellRender = slots?.dateCellRender, monthFullCellRender = slots?.monthFullCellRender, monthCellRender = slots?.monthCellRender, headerRender = slots?.headerRender, subHeaderRender = slots?.subHeaderRender, defaultHeaderLeftMoreRender = slots?.defaultHeaderLeftMoreRender, fullscreen = true, validRange, quotationColor, } = props;
                // ====================== Render ======================
                const dateRender = ({ current: date }) => {
                    if (dateFullCellRender) {
                        return dateFullCellRender({ current: date });
                    }
                    return (<div class={classNames(`${prefixCls.value}-cell-inner`, `${calendarPrefixCls.value}-date`, {
                            [`${calendarPrefixCls.value}-date-today`]: isSameDate(today, date),
                        }, {
                            [`${calendarPrefixCls.value}-amount`]: props.showAmount,
                        }, {
                            [`${calendarPrefixCls.value}-future`]: props.disabledDate?.(date),
                        }, {
                            [`${calendarPrefixCls.value}-zero`]: props.showAmount &&
                                [0, null, '--'].includes(getCurMonthValue(dayjs(date).format('YYYY/MM/DD'), date, 'value')),
                        }, {
                            [`${calendarPrefixCls.value}-close-market`]: props.showAmount &&
                                getCurMonthValue(dayjs(date).format('YYYY/MM/DD'), date, 'closeTheMarket'),
                        }, {
                            [`${calendarPrefixCls.value}-${quotationColor === QuotationColor.RED_UP_GREEN_DOWN ? 'ru' : 'gu'}-${getCurMonthValue(dayjs(date).format('YYYY/MM/DD'), date, 'value') > 0
                                ? 'up'
                                : 'down'}`]: props.showAmount &&
                                !props.disabledDate(date) &&
                                ![0, null, '--'].includes(getCurMonthValue(dayjs(date).format('YYYY/MM/DD'), date, 'value')),
                        })}>
              <div class={`${calendarPrefixCls.value}-date-value`}>
                {padStart(String(generateConfig.getDate(date)), 2, '0')}
              </div>
              <div class={`${calendarPrefixCls.value}-date-content`}>
                {props.showAmount
                            ? getCurMonthValue(dayjs(date).format('YYYY/MM/DD'), date, 'valueText')
                            : dateCellRender && dateCellRender({ current: date })}
              </div>
            </div>);
                };
                const monthRender = ({ current: date }) => {
                    if (monthFullCellRender) {
                        return monthFullCellRender({ current: date });
                    }
                    return (<div class={classNames(`${prefixCls.value}-cell-inner`, `${calendarPrefixCls.value}-date`, {
                            [`${calendarPrefixCls.value}-date-today`]: isSameMonth(today, date),
                        }, {
                            [`${calendarPrefixCls.value}-amount`]: props.showAmount,
                        }, {
                            [`${calendarPrefixCls.value}-future`]: props.disabledDate?.(date),
                        }, {
                            [`${calendarPrefixCls.value}-zero`]: props.showAmount &&
                                [0, null, '--'].includes(getCurMonthValue(dayjs(date).format('YYYY/MM'), date, 'value', 'YYYY/MM')),
                        }, {
                            [`${calendarPrefixCls.value}-${quotationColor === QuotationColor.RED_UP_GREEN_DOWN ? 'ru' : 'gu'}-${getCurMonthValue(dayjs(date).format('YYYY/MM'), date, 'value', 'YYYY/MM') > 0
                                ? 'up'
                                : 'down'}`]: props.showAmount &&
                                !props.disabledDate(date) &&
                                ![0, null, '--'].includes(getCurMonthValue(dayjs(date).format('YYYY/MM'), date, 'value', 'YYYY/MM')),
                        })}>
              <div class={`${calendarPrefixCls.value}-date-value`}>
                {padStart(String(generateConfig.getMonth(date) + 1), 2, '0')}
              </div>
              <div class={`${calendarPrefixCls.value}-date-content`}>
                {props.showAmount
                            ? getCurMonthValue(dayjs(date).format('YYYY/MM'), date, 'valueText', 'YYYY/MM')
                            : monthCellRender && monthCellRender({ current: date })}
              </div>
            </div>);
                };
                return (<div {...attrs} class={classNames(calendarPrefixCls.value, {
                        [`${calendarPrefixCls.value}-full`]: fullscreen,
                        [`${calendarPrefixCls.value}-mini`]: !fullscreen,
                        [`${calendarPrefixCls.value}-rtl`]: direction.value === 'rtl',
                    }, attrs.class)}>
            {headerRender ? (headerRender({
                        value: mergedValue.value,
                        type: mergedMode.value,
                        onChange: onInternalSelect,
                        onTypeChange: triggerModeChange,
                    })) : (<CalendarHeader prefixCls={calendarPrefixCls.value} value={mergedValue.value} generateConfig={generateConfig} mode={mergedMode.value} fullscreen={fullscreen} locale={mergedLocale.value.lang} validRange={validRange} disabledDate={props.disabledDate} defaultHeaderLeftMoreRender={defaultHeaderLeftMoreRender ? slots.defaultHeaderLeftMoreRender?.() : ''} onChange={onInternalSelect} onModeChange={triggerModeChange} onSelectCurMonth={onSelectCurMonth}/>)}
            {subHeaderRender ? slots.subHeaderRender?.() : ''}
            <div class={`${prefixCls.value}-wrapper`}>
              <PickerPanel value={mergedValue.value} prefixCls={prefixCls.value} locale={mergedLocale.value.lang} generateConfig={generateConfig} dateRender={dateRender} monthCellRender={obj => monthRender(obj)} onSelect={onInternalSelect} mode={panelMode.value} hiddenWeekend={props.hiddenWeekend} height={props.height} noEvent={props.noEvent} picker={panelMode.value} disabledDate={mergedDisabledDate.value} fromCalendar hideHeader/>
            </div>
          </div>);
            };
        },
    });
    Calendar.install = function (app) {
        app.component(Calendar.name, Calendar);
        return app;
    };
    return Calendar;
}
export default generateCalendar;
